<template>
  <div>
    <!-- a scheduled run posts to slack (nobody is at a browser to save its pdfs): the delivery is
         part of the dialog so the schedule can be turned on from here; the editor has it above -->
    <div v-if="showDelivery" class="d-flex align-center flex-wrap macro-options">
      <v-select v-model="draft.delivery" :items="deliveries" label="Pdfs go to" hide-details :disabled="!slack.configured"
                data-cy="macro-edit-delivery"/>
      <SlackChannelPicker
        v-if="slack.configured && draft.delivery === 'slack'"
        v-model="draft.slackChannel"
        :channels="channels"
      />
      <div v-if="!slack.configured" class="caption grey--text mt-1">the api has no Slack bot set up</div>
    </div>
    <v-checkbox
      v-model="draft.schedule.enabled"
      label="Run on a schedule"
      :disabled="!!blockedReason"
      hide-details
      dense
      class="mt-4"
      data-cy="macro-edit-schedule"
    />
    <v-alert v-if="blockedReason" type="info" text dense class="mt-2 mb-0" data-cy="macro-edit-schedule-blocked">
      {{ blockedReason }}
    </v-alert>
    <template v-if="draft.schedule.enabled">
      <!-- as many days as it should run on -->
      <v-btn-toggle v-model="draft.schedule.days" multiple dense color="primary" class="mt-3 flex-wrap"
                    data-cy="macro-edit-schedule-days">
        <v-btn v-for="day of weekdays" v-bind:key="day.value" :value="day.value" small
               :data-cy="`macro-edit-schedule-day-${day.value}`">
          {{ day.text.slice(0, 3) }}
        </v-btn>
      </v-btn-toggle>
      <div class="d-flex align-start flex-wrap mt-3 macro-options">
        <v-text-field
          v-model="draft.schedule.time"
          type="time"
          label="When should this happen"
          hint="Vancouver time"
          persistent-hint
          dense
          outlined
          data-cy="macro-edit-schedule-time"
        />
        <v-select
          v-model="draft.schedule.production"
          :items="scheduleProductions"
          label="Prints for"
          hint="the next runs below show which days each choice prints for"
          persistent-hint
          dense
          outlined
          class="macro-schedule-production"
          data-cy="macro-edit-schedule-production"
        />
      </div>
      <div class="caption grey--text mt-2" data-cy="macro-edit-schedule-examples">
        <template v-if="examples.length">
          The next runs would print:
          <ul>
            <li v-for="run of examples" v-bind:key="run.at.getTime()">
              {{ formatRunTime(run.at) }} &rarr; {{ formatRunDays(run.dates) }}
            </li>
          </ul>
        </template>
        <template v-else>{{ scheduleProblem(draft.schedule) }}</template>
        The api runs it and posts the pdfs to the Slack channel.
        <template v-if="draft.schedule.runAs">It runs as {{ draft.schedule.runAs.email }}, who turned it on.</template>
        <template v-else>It will run as you, with your pages.</template>
      </div>
    </template>
  </div>
</template>

<script>
/**
 * The schedule of a print macro as it is edited: the days, time and production it runs for,
 * with the next runs as examples. Edits a draft in place ({schedule, delivery, slackChannel}),
 * like the macro editor it sits in; the "Run on schedule" dialog uses it on a draft of its own.
 */
import moment from 'moment/moment';
import SlackChannelPicker from '@/components/SlackChannelPicker.vue';
import {SCHEDULE_PRODUCTIONS, scheduleProblem, upcomingRuns, WEEKDAYS} from '@/utils/macro-schedule';

export default {
  name: 'MacroScheduleFields',
  components: {SlackChannelPicker},
  props: {
    draft: {type: Object, required: true},
    // the slack channels to pick from, and whether the api can post there ({configured, channel})
    channels: {type: Array, default: () => []},
    slack: {type: Object, required: true},
    showDelivery: {type: Boolean, default: false},
  },
  data() {
    return {
      weekdays: WEEKDAYS,
      scheduleProductions: SCHEDULE_PRODUCTIONS,
      deliveries: [
        {text: 'Download here', value: 'download'},
        {text: 'Post to Slack', value: 'slack'},
      ],
    };
  },
  computed: {
    /** why the macro cannot run on a schedule; '' when it can */
    blockedReason() {
      // nobody is at a browser to save the pdfs of a scheduled run, so they can only go to slack
      if (!this.slack.configured) {
        return 'Scheduled runs post their pdfs to Slack, and the api has no Slack bot set up, so this macro cannot run on a schedule.';
      }
      if (this.draft.delivery !== 'slack') {
        return 'Scheduled runs post their pdfs to Slack, as nobody is at a browser to download them. '
          + 'Set "Pdfs go to" to Post to Slack and choose a channel to turn this on.';
      }
      if (!this.draft.slackChannel) {
        return 'Choose the Slack channel to turn this on: scheduled runs post their pdfs there.';
      }
      return '';
    },
    /** the next few runs of the schedule, with the days each would print for */
    examples() {
      return this.draft.schedule.enabled ? upcomingRuns(this.draft.schedule, new Date(), 4) : [];
    },
  },
  watch: {
    /** leaving Slack (or its channel) turns the schedule off; its days and time are kept for turning it back on */
    blockedReason(reason) {
      if (reason) {
        this.draft.schedule.enabled = false;
      }
    },
  },
  methods: {
    scheduleProblem,
    formatRunTime(date) {
      return moment(date).format('ddd MMM D HH:mm');
    },
    /** e.g. 'Mon Sep 28 to Wed Sep 30' */
    formatRunDays(dates) {
      return `${moment(dates[0]).format('ddd MMM D')} to ${moment(dates[dates.length - 1]).format('ddd MMM D')}`;
    },
  },
};
</script>

<style scoped>
/* the selects side by side with a little air between them, wrapping on a narrow column */
.macro-options > * {
  flex: 1 1 200px;
  max-width: 260px;
  margin: 4px 12px 4px 0;
}

.macro-options > .macro-schedule-production {
  max-width: 320px;
}
</style>
